import React, { useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { Send, Sparkles, Loader2, Check, X, CheckCheck, ChevronDown, ChevronRight, FileText, Brain, Database, User, Zap, MessageCircle } from 'lucide-react';
import LoopraLoading from './LoopraLoading';
import type { GeneratedScript, ScriptGenerationProgress } from '../../types/aiGeneration';
import { scriptsApi, configApi } from '../../services/api';
import { useTheme } from 'styled-components';
import { showSuccessToast, showErrorToast } from '../../utils/toastHelpers';
import { motion, AnimatePresence } from 'framer-motion';
import { useQueryClient } from '@tanstack/react-query';

// Declaração global para evitar duplicação de toasts
declare global {
  interface Window {
    __loopraToastLock?: boolean;
  }
}

// 🎨 Animações Ultra Profissionais
const shimmer = keyframes`
  0% { 
    background-position: -1000px 0;
    opacity: 0.3;
  }
  50% {
    opacity: 0.8;
  }
  100% { 
    background-position: 1000px 0;
    opacity: 0.3;
  }
`;

const glowPulse = keyframes`
  0%, 100% { 
    box-shadow: 0 0 20px rgba(103, 93, 255, 0.3),
                0 0 40px rgba(103, 93, 255, 0.2),
                0 0 60px rgba(103, 93, 255, 0.1);
  }
  50% { 
    box-shadow: 0 0 30px rgba(103, 93, 255, 0.5),
                0 0 60px rgba(103, 93, 255, 0.3),
                0 0 90px rgba(103, 93, 255, 0.2);
  }
`;

const fadeIn = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(40px) scale(0.95);
    filter: blur(10px);
  }
  to { 
    opacity: 1; 
    transform: translateY(0) scale(1);
    filter: blur(0);
  }
`;

const fadeInUp = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(30px);
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
`;

const smoothPulse = keyframes`
  0%, 100% { 
    opacity: 1;
    transform: scale(1);
  }
  50% { 
    opacity: 0.6;
    transform: scale(1.05);
  }
`;

const revealText = keyframes`
  from {
    opacity: 0;
    filter: blur(12px);
    transform: scale(0.95) translateY(10px);
  }
  to {
    opacity: 1;
    filter: blur(0);
    transform: scale(1) translateY(0);
  }
`;

const zoomIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.85);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const slideInFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const floatAnimation = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const gradientRotate = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

// Tipos de templates para scripts (usando mesmos ícones da seção de scripts)
const SCRIPT_TEMPLATES = {
  conteudo: {
    title: 'Scripts de Conteúdo',
    description: 'Respostas principais do bot',
    icon: FileText,
  },
  mentalidade: {
    title: 'Mentalidades',
    description: 'Personalidade e comportamento',
    icon: Brain,
  },
  fatos_produto: {
    title: 'Informações do Produto',
    description: 'Template: Nome, Tipo, Foco, Escopo',
    icon: Database,
  },
  persona: {
    title: 'Configuração de Persona',
    description: 'Identidade e tom do bot',
    icon: User,
  },
  linguagem: {
    title: 'Linguagem',
    description: 'Gírias e abreviações permitidas',
    icon: MessageCircle,
  },
  emojis: {
    title: 'Emojis',
    description: 'Paleta de emojis contextuais',
    icon: Sparkles,
  },
};

// 🎨 Container Principal - Design Ultra Profissional
const GeneratorContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius['2xl']};
  overflow: hidden;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3),
              0 0 100px rgba(103, 93, 255, 0.1);
  
  /* Efeito de brilho sutil */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      ${({ theme }) => theme.colors.primary}60,
      transparent
    );
    opacity: 0.5;
  }
`;

// 🎨 Header Ultra Elegante com Glassmorphism
const ChatHeader = styled.div`
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[5]};
  background: linear-gradient(
    135deg,
    ${({ theme }) => `${theme.colors.backgroundSecondary}f5`} 0%,
    ${({ theme }) => `${theme.colors.backgroundTertiary}f5`} 100%
  );
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  
  /* Borda superior brilhante */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 10%;
    right: 10%;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      ${({ theme }) => theme.colors.primary}40,
      transparent
    );
  }
  
  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  }
`;

const ChatLogo = styled.div`
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  
  /* Anel de brilho ao redor do logo */
  &::before {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.colors.primary}30,
      ${({ theme }) => theme.colors.primary}10
    );
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::before {
    opacity: 1;
  }
  
  img {
    width: 64px;
    height: 64px;
    object-fit: contain;
    filter: drop-shadow(0 2px 12px ${({ theme }) => theme.colors.primary}80);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  &:hover img {
    transform: scale(1.08) rotate(5deg);
    filter: drop-shadow(0 4px 18px ${({ theme }) => theme.colors.primary}A0);
  }
  
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    img {
      width: 40px;
      height: 40px;
    }
  }
`;

const ChatTitle = styled.div`
  flex: 2;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  h3 {
    margin: 0;
    font-family: ${({ theme }) => theme.typography.fontFamily.heading};
    font-size: 2.2rem;
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.colors.textPrimary} 0%,
      ${({ theme }) => theme.colors.primary} 100%
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[2]};
    letter-spacing: -0.02em;
    
    svg {
      -webkit-text-fill-color: ${({ theme }) => theme.colors.primary};
      filter: drop-shadow(0 0 6px ${({ theme }) => theme.colors.primary}40);
    }
  }
  
  p {
    margin: 4px 0 0 0;
    font-size: 1.1rem;
    font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
    color: ${({ theme }) => theme.colors.textSecondary};
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    opacity: 0.9;
    letter-spacing: 0.02em;
    text-transform: none;
    max-width: 100%;
  }
  
  @media (max-width: 768px) {
    h3 {
      font-size: 1.2rem;
    }
    p {
      font-size: 0.9rem;
    }
  }
`;

// 🎨 Área de Prompt Ultra Profissional com Glassmorphism
const PromptArea = styled.div`
  padding: ${({ theme }) => theme.spacing[5]};
  background: linear-gradient(
    180deg,
    ${({ theme }) => `${theme.colors.backgroundSecondary}f8`} 0%,
    ${({ theme }) => `${theme.colors.backgroundTertiary}f8`} 100%
  );
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  position: relative;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing[4]};
  }
  
  /* Borda superior brilhante */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 10%;
    right: 10%;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      ${({ theme }) => theme.colors.primary}60,
      transparent
    );
  }
  
  /* Efeito de shimmer sutil */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 200%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      ${({ theme }) => theme.colors.primary}05,
      transparent
    );
    animation: ${shimmer} 4s ease-in-out infinite;
    pointer-events: none;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  align-items: center;
`;

const InputField = styled.div`
  flex: 1;
  position: relative;
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  min-height: 70px;
  max-height: 180px;
  padding: ${({ theme }) => theme.spacing[4]};
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.xl};
  background: ${({ theme }) => `${theme.colors.background}f5`};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  resize: vertical;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  line-height: 1.6;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);

  &::placeholder {
    color: ${({ theme }) => theme.colors.textTertiary};
    font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
    opacity: 0.7;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary}40;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12);
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.background};
    box-shadow: 0 0 0 4px ${({ theme }) => `${theme.colors.primary}20`},
                0 8px 32px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    min-height: 60px;
    padding: ${({ theme }) => theme.spacing[3]};
  }
`;

const GenerateButton = styled.button<{ $disabled: boolean }>`
  padding: ${({ theme }) => theme.spacing[4]} ${({ theme }) => theme.spacing[8]};
  border-radius: ${({ theme }) => theme.borders.radius.xl};
  background: ${({ theme, $disabled }) =>
    $disabled
      ? theme.colors.backgroundTertiary
      : `linear-gradient(135deg, 
          ${theme.colors.primary} 0%, 
          ${theme.colors.primaryHover} 50%,
          ${theme.colors.primary} 100%)`};
  background-size: 200% 100%;
  border: none;
  color: ${({ $disabled }) => ($disabled ? 'inherit' : 'white')};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]};
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${({ theme, $disabled }) => 
    $disabled 
      ? 'none' 
      : `0 8px 24px ${theme.colors.primary}40,
         0 4px 12px ${theme.colors.primary}30,
         inset 0 1px 0 rgba(255,255,255,0.1)`};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  white-space: nowrap;
  align-self: center;
  position: relative;
  overflow: hidden;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  
  /* Efeito de brilho interno */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s ease;
  }
  
  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[6]};
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
  }

  &:hover:not(:disabled) {
    transform: translateY(-3px) scale(1.02);
    box-shadow: 0 12px 32px ${({ theme }) => theme.colors.primary}50,
                0 6px 16px ${({ theme }) => theme.colors.primary}40,
                inset 0 1px 0 rgba(255,255,255,0.2);
    background-position: 100% 0;
    
    &::before {
      left: 100%;
    }
  }

  &:active:not(:disabled) {
    transform: translateY(-1px) scale(0.98);
    box-shadow: 0 4px 16px ${({ theme }) => theme.colors.primary}40;
  }

  &:disabled {
    cursor: not-allowed;
  }
  
  svg {
    filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2));
  }
`;

// Empty State (Placeholder GPT-style)
const EmptyState = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: ${({ theme }) => theme.spacing[8]};
  
  h2 {
    font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.colors.textPrimary} 0%,
      ${({ theme }) => theme.colors.primary} 100%
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0;
    text-align: center;
    letter-spacing: -0.02em;
  }
  
  svg {
    margin-bottom: ${({ theme }) => theme.spacing[4]};
    opacity: 0.6;
    animation: ${floatAnimation} 3s ease-in-out infinite;
  }
`;

// Área de geração e resultados (compacta)
const ResultsArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing[4]};
  min-height: 300px;
  max-height: 600px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borders.radius.full};

    &:hover {
      background: ${({ theme }) => theme.colors.primary};
    }
  }
  
  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing[3]};
    max-height: 400px;
  }
`;

// Progresso de geração
const GenerationProgress = styled.div`
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.primary}30;
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  padding: ${({ theme }) => theme.spacing[6]};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
  animation: ${fadeIn} 0.4s ease-out;
`;

const ProgressHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const ProgressIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.primaryHover} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${smoothPulse} 1.5s ease-in-out infinite;
`;

const ProgressText = styled.div`
  flex: 1;
`;

const ProgressTitle = styled.h4`
  margin: 0 0 ${({ theme }) => theme.spacing[1]} 0;
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const ProgressSubtitle = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ProgressBar = styled.div`
  height: 6px;
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-radius: ${({ theme }) => theme.borders.radius.full};
  overflow: hidden;
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

const ProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background: linear-gradient(90deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.primaryHover} 100%);
  transition: width 0.3s ease-out;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    animation: ${shimmer} 1.5s infinite;
  }
`;

// Chain of Thought (glassmorphism + compacto)
const ThinkingSection = styled.div<{ $expanded?: boolean }>`
  background: ${({ theme }) => `${theme.colors.backgroundTertiary}95`};
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid ${({ theme }) => `${theme.colors.primary}30`};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  margin-top: ${({ theme }) => theme.spacing[3]};
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  user-select: none;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);

  &:hover {
    background: ${({ theme }) => `${theme.colors.backgroundTertiary}cc`};
    border-color: ${({ theme }) => `${theme.colors.primary}50`};
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  }
`;

const ThinkingHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
`;

const ThinkingTitle = styled.h5`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ThinkingContent = styled.div<{ $expanded: boolean }>`
  max-height: ${({ $expanded }) => ($expanded ? '200px' : '0')};
  opacity: ${({ $expanded }) => ($expanded ? 1 : 0)};
  padding: ${({ $expanded, theme }) => ($expanded ? `0 ${theme.spacing[4]} ${theme.spacing[3]}` : '0')};
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
`;

const ThinkingText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textTertiary};
  line-height: 1.5;
  font-family: 'Inter', system-ui, sans-serif;
  animation: ${fadeInUp} 0.6s ease-out;
`;

// Scripts gerados
const ScriptsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const ScriptCard = styled.div<{ $status: string }>`
  background: ${({ theme }) => `${theme.colors.backgroundSecondary}f8`};
  backdrop-filter: blur(10px) saturate(150%);
  -webkit-backdrop-filter: blur(10px) saturate(150%);
  border: 2px solid ${({ theme, $status }) =>
    $status === 'approved'
      ? theme.colors.error
      : $status === 'rejected'
      ? theme.colors.error
      : theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.xl};
  overflow: hidden;
  animation: ${fadeIn} 1.2s cubic-bezier(0.34, 1.56, 0.64, 1) backwards;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 1;
  transform: translateY(0);
  position: relative;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  /* Brilho sutil no topo */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      ${({ theme, $status }) =>
        $status === 'approved'
          ? theme.colors.error
          : $status === 'rejected'
          ? theme.colors.error
          : theme.colors.primary}60,
      transparent
    );
    opacity: 0.5;
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
    border-color: ${({ theme, $status }) =>
      $status === 'approved'
        ? theme.colors.error
        : $status === 'rejected'
        ? theme.colors.error
        : theme.colors.primary}80;
  }
  
  /* Delay progressivo suave */
  &:nth-child(1) { animation-delay: 0.2s; }
  &:nth-child(2) { animation-delay: 0.5s; }
  &:nth-child(3) { animation-delay: 0.8s; }
  &:nth-child(4) { animation-delay: 1.1s; }
  &:nth-child(5) { animation-delay: 1.4s; }
  &:nth-child(6) { animation-delay: 1.7s; }
  &:nth-child(7) { animation-delay: 2s; }
  &:nth-child(8) { animation-delay: 2.3s; }
`;

const ScriptCardHeader = styled.div`
  padding: ${({ theme }) => theme.spacing[5]};
  background: linear-gradient(
    135deg,
    ${({ theme }) => `${theme.colors.backgroundTertiary}cc`} 0%,
    ${({ theme }) => `${theme.colors.backgroundSecondary}cc`} 100%
  );
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-bottom: 1px solid ${({ theme }) => `${theme.colors.border}80`};
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;

  &:hover {
    background: linear-gradient(
      135deg,
      ${({ theme }) => `${theme.colors.background}f5`} 0%,
      ${({ theme }) => `${theme.colors.backgroundSecondary}f5`} 100%
    );
    border-bottom-color: ${({ theme }) => theme.colors.primary}40;
  }
`;

const ScriptCardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  flex: 1;
`;

const ScriptTypeIcon = styled.div<{ $scriptType: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.borders.radius.md};
  background: ${props => {
    switch (props.$scriptType) {
      case 'conteudo': return 'linear-gradient(135deg, #6b7280, #4b5563)';
      case 'mentalidade': return 'linear-gradient(135deg, #ef4444, #dc2626)';
      case 'fatos_produto': return 'linear-gradient(135deg, #f59e0b, #d97706)';
      case 'persona': return 'linear-gradient(135deg, #8b5cf6, #7c3aed)'; // Roxo
      case 'linguagem': return 'linear-gradient(135deg, #10b981, #059669)'; // Verde
      case 'emojis': return 'linear-gradient(135deg, #f472b6, #ec4899)'; // Rosa
      default: return props.theme.colors.backgroundTertiary;
    }
  }};
  color: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  flex-shrink: 0;
  will-change: transform;
  backface-visibility: hidden;
  transform: translateZ(0);

  ${props => {
    const animations = {
      conteudo: 'fadeIn 0.6s ease-in-out',
      mentalidade: 'pulse 2s ease-in-out infinite',
      fatos_produto: 'scale 0.8s ease-in-out',
      persona: 'pulse 2s ease-in-out infinite',
      linguagem: 'fadeIn 0.6s ease-in-out',
      emojis: 'scale 0.8s ease-in-out'
    };
    return `animation: ${animations[props.$scriptType as keyof typeof animations] || 'none'};`;
  }}

  @keyframes pulse {
    0%, 100% { transform: scale(1) translateZ(0); }
    50% { transform: scale(1.05) translateZ(0); }
  }

  @keyframes rotate {
    from { transform: rotate(0deg) translateZ(0); }
    to { transform: rotate(360deg) translateZ(0); }
  }

  @keyframes scale {
    0%, 100% { transform: scale(1) translateZ(0); }
    50% { transform: scale(1.1) translateZ(0); }
  }
`;

const ScriptInfo = styled.div`
  flex: 1;
`;

const ScriptName = styled.h4`
  margin: 0 0 ${({ theme }) => theme.spacing[1]} 0;
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const ScriptType = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textTertiary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ScriptActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const IconButton = styled.button<{ $variant?: 'success' | 'error' | 'default' | 'primary' }>`
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.borders.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme, $variant }) => {
    switch ($variant) {
      case 'success':
        return theme.colors.success + '20';
      case 'error':
        return theme.colors.error + '20';
      case 'primary':
        return theme.colors.primary + '20';
      default:
        return theme.colors.backgroundTertiary;
    }
  }};
  color: ${({ theme, $variant }) => {
    switch ($variant) {
      case 'success':
        return theme.colors.success;
      case 'error':
        return theme.colors.error;
      case 'primary':
        return theme.colors.primary;
      default:
        return theme.colors.textSecondary;
    }
  }};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${({ theme }) => theme.animations.duration.fast};

  &:hover {
    transform: translateY(-2px) scale(1.05);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }
  
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
`;

const ScriptCardContent = styled.div<{ $expanded: boolean }>`
  max-height: ${({ $expanded }) => ($expanded ? '1000px' : '0')};
  overflow: hidden;
  transition: max-height 0.8s cubic-bezier(0.4, 0, 0.2, 1), transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${({ $expanded }) => ($expanded ? zoomIn : 'none')} 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: top center;
`;

const ScriptPreview = styled.div`
  padding: ${({ theme }) => theme.spacing[5]};
  background: ${({ theme }) => theme.colors.background};
  animation: ${fadeInUp} 0.7s ease-out;
`;

const PreviewLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textTertiary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  animation: ${fadeInUp} 0.6s ease-out;
`;

const PreviewContent = styled.pre`
  margin: 0;
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-family: 'Monaco', 'Menlo', monospace;
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 300px;
  overflow-y: auto;
  animation: ${revealText} 1s cubic-bezier(0.4, 0, 0.2, 1);

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.background};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borders.radius.full};
  }
`;

const ReasoningSection = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundTertiary}50;
`;

const ReasoningLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textTertiary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const ReasoningText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
`;

// 🎨 Barra de Aprovar Todos - Design Ultra Premium
const ApproveAllBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  padding: ${({ theme }) => theme.spacing[6]};
  margin-bottom: ${({ theme }) => theme.spacing[5]};
  background: linear-gradient(
    135deg,
    ${({ theme }) => `${theme.colors.error}15`} 0%,
    ${({ theme }) => `${theme.colors.error}10`} 100%
  );
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 2px solid ${({ theme }) => theme.colors.error}60;
  border-radius: ${({ theme }) => theme.borders.radius['2xl']};
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 8px 32px rgba(239, 68, 68, 0.2),
              0 4px 16px rgba(0, 0, 0, 0.1);
  animation: ${fadeInUp} 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  /* Brilho animado no topo */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent,
      ${({ theme }) => theme.colors.error},
      transparent
    );
    animation: ${shimmer} 2s ease-in-out infinite;
  }
  
  /* Brilho de fundo sutil */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at top right,
      ${({ theme }) => theme.colors.error}10,
      transparent 50%
    );
    pointer-events: none;
  }
`;

const ApproveAllInfo = styled.div`
  position: relative;
  z-index: 1;
`;

const ApproveAllTitle = styled.h4`
  margin: 0 0 ${({ theme }) => theme.spacing[2]} 0;
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  letter-spacing: -0.01em;
`;

const ApproveAllSubtitle = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
  opacity: 0.9;
`;

const ApproveAllButton = styled.button<{ $disabled: boolean }>`
  padding: ${({ theme }) => theme.spacing[5]} ${({ theme }) => theme.spacing[10]};
  border-radius: ${({ theme }) => theme.borders.radius.xl};
  background: ${({ theme, $disabled }) =>
    $disabled
      ? theme.colors.backgroundTertiary
      : `linear-gradient(135deg, ${theme.colors.error} 0%, #ef4444 50%, ${theme.colors.error} 100%)`};
  background-size: 200% 100%;
  border: none;
  color: white;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  box-shadow: ${({ $disabled }) => 
    $disabled 
      ? 'none' 
      : `0 8px 24px rgba(239, 68, 68, 0.35),
         0 4px 12px rgba(239, 68, 68, 0.25),
         inset 0 1px 0 rgba(255,255,255,0.2)`};
  position: relative;
  z-index: 1;
  overflow: hidden;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  
  /* Efeito de brilho interno */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    transition: left 0.5s ease;
  }

  &:hover:not(:disabled) {
    transform: translateY(-3px) scale(1.03);
    box-shadow: 0 12px 32px rgba(239, 68, 68, 0.45),
                0 6px 16px rgba(239, 68, 68, 0.35),
                inset 0 1px 0 rgba(255,255,255,0.3);
    background-position: 100% 0;
    
    &::before {
      left: 100%;
    }
  }

  &:active:not(:disabled) {
    transform: translateY(-1px) scale(0.98);
    box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4);
  }
  
  svg {
    filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2));
  }
`;

// Modal Components (seguindo padrão do projeto)
const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => (theme.isLight ? 'rgba(16,24,32,0.06)' : theme.colors.overlay)};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled(motion.div)`
  background: ${({ theme }) => theme.colors.backgroundSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.xl};
  width: 90%;
  max-width: 450px;
  max-height: 70vh;
  box-shadow: ${({ theme }) => theme.shadows.xl};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    width: 95%;
    max-width: none;
    max-height: 75vh;
    margin: ${({ theme }) => theme.spacing[3]};
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing[3]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.backgroundTertiary} 0%,
    ${({ theme }) => theme.colors.backgroundSecondary} 100%
  );
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing[2]};
  }
`;

const ModalTitle = styled.h2`
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  
  @media (max-width: 768px) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color ${({ theme }) => theme.animations.duration.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const ModalBody = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
  overflow-y: auto;
  flex: 1;
  
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.backgroundTertiary};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borders.radius.full};

    &:hover {
      background: ${({ theme }) => theme.colors.primary};
    }
  }
  
  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing[2]};
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing[2]};
  }
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'success' | 'cancel' }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast};
  border: 1px solid transparent;
  
  svg {
    width: 16px;
    height: 16px;
  }

  ${({ theme, $variant }) => {
    switch ($variant) {
      case 'success':
        return `
          background: linear-gradient(135deg, ${theme.colors.success} 0%, #22c55e 100%);
          color: white;
          box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(34, 197, 94, 0.4);
          }
        `;
      case 'primary':
        return `
          background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryHover} 100%);
          color: white;
          box-shadow: ${theme.shadows.neon};
          &:hover {
            transform: translateY(-2px);
            box-shadow: ${theme.shadows.lg};
          }
        `;
      default:
        return `
          background: ${theme.colors.backgroundSecondary};
          color: ${theme.colors.textSecondary};
          border-color: ${theme.colors.border};
          &:hover {
            background: ${theme.colors.backgroundTertiary};
            color: ${theme.colors.textPrimary};
            border-color: ${theme.colors.primary};
          }
        `;
    }
  }}
`;

const SummarySection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[2]};
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.lg};
  transition: all ${({ theme }) => theme.animations.duration.fast};
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary}40;
    box-shadow: 0 2px 8px ${({ theme }) => theme.colors.primary}10;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing[1]};
  }
`;

const SummaryTitle = styled.h4`
  margin: 0 0 ${({ theme }) => theme.spacing[1]} 0;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.textPrimary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  svg {
    width: 14px;
    height: 14px;
  }
  
  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

const SummaryText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.4;
  max-height: 80px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borders.radius.full};
  }
  
  @media (max-width: 768px) {
    font-size: 11px;
    max-height: 60px;
  }
`;

const ExampleBox = styled.div`
  margin-top: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => theme.spacing[2]};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textPrimary};
  line-height: 1.4;
  font-style: italic;
  max-height: 80px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borders.radius.full};
  }
  
  @media (max-width: 768px) {
    font-size: ${({ theme }) => theme.typography.fontSize.xs};
    padding: ${({ theme }) => theme.spacing[2]};
    max-height: 80px;
  }
`;

interface ScriptGeneratorProps {
  userId?: string;
  onApproveAll: (scripts: GeneratedScript[]) => Promise<void>;
  onApproveScript: (script: GeneratedScript) => Promise<void>;
}

const ScriptGenerator: React.FC<ScriptGeneratorProps> = ({ userId, onApproveAll, onApproveScript }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState<ScriptGenerationProgress | null>(null);
  const [generatedScripts, setGeneratedScripts] = useState<GeneratedScript[]>([]);
  const [expandedScripts, setExpandedScripts] = useState<Set<string>>(new Set());
  const [isThinkingExpanded, setIsThinkingExpanded] = useState(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Estados dos modais
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'one' | 'all'>('one');
  const [selectedScript, setSelectedScript] = useState<GeneratedScript | null>(null);
  const [attendantSummary, setAttendantSummary] = useState<{
    name: string;
    personality: string;
    example: string;
  } | null>(null);

  const handleGenerate = async () => {
    if (!prompt.trim() || isGenerating) return;

    // Limpar input imediatamente após enviar
    const userPrompt = prompt;
    setPrompt('');
    
    // Detectar se é primeira geração ou atualização
    const hasExistingScripts = generatedScripts.length > 0;
    const isUpdate = hasExistingScripts;
    
    setIsGenerating(true);
    
    // Se for primeira geração, limpar scripts. Se for atualização, manter
    if (!isUpdate) {
      setGeneratedScripts([]);
    }
    
    setProgress({
      currentScript: isUpdate ? 'Iniciando atualização incremental...' : 'Iniciando geração inteligente...',
      progress: 10,
      totalSteps: 100,
      reasoning: isUpdate ? 
        'Analisando suas instruções de atualização e carregando scripts existentes...' :
        'Analisando sua solicitação e conectando com a inteligência artificial...',
    });

    try {
      // Simular progresso natural
      await new Promise((resolve) => setTimeout(resolve, 300));
      setProgress({
        currentScript: isUpdate ? 'Processando atualizações...' : 'Processando contexto...',
        progress: 25,
        totalSteps: 100,
        reasoning: isUpdate ?
          'Identificando quais scripts precisam ser atualizados conforme suas instruções...' :
          'Identificando nome do atendente, produto, tom de voz e tipo de negócio...',
      });

      await new Promise((resolve) => setTimeout(resolve, 400));
      setProgress({
        currentScript: isUpdate ? 'Aplicando mudanças...' : 'Gerando scripts personalizados...',
        progress: 40,
        totalSteps: 100,
        reasoning: isUpdate ?
          'Aplicando suas modificações mantendo o contexto e personalidade originais...' :
          'Criando saudações, apresentações e benefícios adaptados ao seu contexto...',
      });

      // Preparar dados para a API (incluir scripts existentes se for atualização)
      const requestData: { prompt: string; existingScripts?: GeneratedScript[] } = { prompt: userPrompt };
      if (isUpdate) {
        requestData.existingScripts = generatedScripts.map(script => ({
          name: script.name,
          type: script.type,
          content: script.content,
          reasoning: script.reasoning,
          status: script.status
        }));
      }

      // Chamar API de IA
      const response = await scriptsApi.generateWithAI(requestData, userId);

      setProgress({
        currentScript: 'Refinando conteúdo...',
        progress: 70,
        totalSteps: 100,
        reasoning: 'Ajustando tom de voz, personalidade e estrutura dos scripts...',
      });

      await new Promise((resolve) => setTimeout(resolve, 300));
      setProgress({
        currentScript: 'Finalizando...',
        progress: 90,
        totalSteps: 100,
        reasoning: 'Validando qualidade e formatando scripts para preview...',
      });


      if (response.status === 'success' && response.data) {
        // Adicionar scripts com delay progressivo e preview automática em cada um
        const scripts = response.data.scripts;
        for (let i = 0; i < scripts.length; i++) {
          await new Promise((resolve) => setTimeout(resolve, 600));
          const scriptWithPreview: GeneratedScript = { ...scripts[i], status: 'preview' as const };
          setGeneratedScripts((prev) => [...prev, scriptWithPreview]);
          // Scroll automático para o último script adicionado
          setTimeout(() => {
            const resultsArea = document.querySelector('[data-results-area]');
            if (resultsArea) {
              resultsArea.scrollTo({
                top: resultsArea.scrollHeight,
                behavior: 'smooth'
              });
            }
          }, 100);
          // Auto-expandir CADA script como preview por 3 segundos
          const scriptId = `${scripts[i].type}-${scripts[i].name}`;
          setTimeout(() => {
            setExpandedScripts(new Set([scriptId]));
            setTimeout(() => {
              const resultsArea = document.querySelector('[data-results-area]');
              if (resultsArea) {
                resultsArea.scrollTo({
                  top: resultsArea.scrollHeight,
                  behavior: 'smooth'
                });
              }
            }, 600);
          }, 200);
          await new Promise((resolve) => setTimeout(() => {
            setExpandedScripts(new Set());
            resolve(undefined);
          }, 3000));
        }
        
        // Extrair informações do atendente dos scripts gerados (SEMPRE tentar criar resumo)
        console.log('📊 Extraindo informações do atendente dos scripts gerados...');
        
        // Buscar mentalidade (mais flexível - qualquer script de mentalidade)
        const mentalidadeScript = scripts.find(s => s.type === 'mentalidade') 
          || scripts.find(s => s.type === 'conteudo' && s.name.includes('mentalidade'));
        
        // Buscar saudação (mais flexível - qualquer saudação)
        const saudacaoScript = scripts.find(s => s.name.includes('saudacao')) 
          || scripts.find(s => s.type === 'conteudo' && (s.name.includes('inicial') || s.name.includes('apresentacao')));
        
        // Extrair nome do atendente do prompt
        const nameMatch = prompt.match(/nome[:\s]+(\w+)/i) 
          || prompt.match(/atendente[:\s]+(\w+)/i)
          || prompt.match(/chamar[:\s]+de[:\s]+(\w+)/i)
          || prompt.match(/(\w+)\s+(delivery|vendedor|atendente|assistente)/i);
        const attendantName = nameMatch ? nameMatch[1] : 'Assistente Virtual Loopra';
        
        // Extrair personalidade (RESUMO CURTO - max 120 caracteres)
        let personality = '';
        if (mentalidadeScript) {
          const content = Array.isArray(mentalidadeScript.content) 
            ? mentalidadeScript.content.join(' ') 
            : mentalidadeScript.content;
          personality = content.substring(0, 120) + (content.length > 120 ? '...' : '');
        } else {
          // Fallback: extrair do prompt
          personality = `Atendente personalizado baseado em: ${userPrompt.substring(0, 80)}...`;
        }
        
        // Extrair exemplo de atuação (EXEMPLO CURTO - max 100 caracteres)
        let example = '';
        if (saudacaoScript) {
          const content = Array.isArray(saudacaoScript.content)
            ? saudacaoScript.content.join(' ')
            : saudacaoScript.content;
          example = content.substring(0, 100) + (content.length > 100 ? '...' : '');
        } else {
          // Fallback: usar primeiro script de conteúdo
          const firstContent = scripts.find(s => s.type === 'conteudo');
          if (firstContent) {
            const content = Array.isArray(firstContent.content)
              ? firstContent.content.join(' ')
              : firstContent.content;
            example = content.substring(0, 100) + (content.length > 100 ? '...' : '');
          } else {
            example = 'Scripts personalizados gerados!';
          }
        }
        
        // SEMPRE definir o resumo do atendente
        setAttendantSummary({
          name: attendantName,
          personality: personality || 'Personalidade customizada baseada em seus scripts.',
          example: example || 'Exemplo de atuação personalizado.',
        });
        
        console.log('✅ Resumo do atendente criado:', {
          name: attendantName,
          hasPersonality: !!personality,
          hasExample: !!example
        });
        
        // Toast único, só se não houver nenhum aprovado ainda
        if (approvedCount === 0) {
          showSuccessToast(
            `✨ ${response.data.totalScripts} scripts gerados com sucesso!`,
            theme
          );
        }
        
        // Mostrar modal de resumo após 1 segundo (SEMPRE)
        setTimeout(() => {
          console.log('🎭 Abrindo modal de resumo do atendente...');
          setShowSummaryModal(true);
        }, 1000);
      } else {
        // Só mostra erro se não for cancelamento do usuário
        if (response.message && !/cancelado|cancelada|canceled/i.test(response.message)) {
          showErrorToast(response.message, theme);
        }
        throw new Error(response.message || 'Erro ao gerar scripts');
      }

      setProgress({
        currentScript: 'Concluído!',
        progress: 100,
        totalSteps: 100,
        reasoning: 'Scripts prontos para revisão e aprovação.',
      });

      await new Promise((resolve) => setTimeout(resolve, 400));
    } catch (error) {
      // Só mostra erro se não for cancelamento do usuário
      const err = error as Error;
      if (err?.message && !/cancelado|cancelada|canceled/i.test(err.message)) {
        showErrorToast(
          'Erro ao gerar scripts. Verifique sua conexão e tente novamente.',
          theme
        );
      }
    } finally {
      setIsGenerating(false);
      setProgress(null);
    }
  };

  const toggleScript = (scriptId: string) => {
    const newExpanded = new Set(expandedScripts);
    if (newExpanded.has(scriptId)) {
      newExpanded.delete(scriptId);
    } else {
      newExpanded.add(scriptId);
    }
    setExpandedScripts(newExpanded);
  };

  const handleApproveClick = (script: GeneratedScript) => {
    setSelectedScript(script);
    setConfirmAction('one');
    setShowConfirmModal(true);
  };

  const handleApproveAllClick = () => {
    setConfirmAction('all');
    setShowConfirmModal(true);
  };

  const handleConfirmApprove = async () => {
    try {
      if (confirmAction === 'one' && selectedScript) {
        await onApproveScript(selectedScript);
        setGeneratedScripts((prev) =>
          prev.map((s) => (s.name === selectedScript.name && s.type === selectedScript.type ? { ...s, status: 'approved' } : s))
        );
        // Toast só se não estiver aprovado ainda
        if (selectedScript.status !== 'approved') {
          showSuccessToast(`Script "${selectedScript.name}" aprovado com sucesso!`, theme);
        }
      } else if (confirmAction === 'all') {
        const scriptsToApprove = generatedScripts.filter((s) => s.status !== 'rejected');

        // Evita duplicação de notificações: apenas UMA notificação no final
        if (!window.__loopraToastLock) {
          window.__loopraToastLock = true;

          await onApproveAll(scriptsToApprove);
          setGeneratedScripts(scriptsToApprove.length > 0 ? scriptsToApprove.map(s => ({ ...s, status: 'approved' })) : []);

          // 🔄 SINCRONIZAR CONFIGURAÇÕES AUTOMATICAMENTE
          try {
            console.log('🔄 Iniciando sincronização automática das configurações...');
            if (userId && scriptsToApprove.length > 0) {
              await configApi.syncFromScripts(userId, scriptsToApprove);
              console.log('✅ Configurações sincronizadas com sucesso!');

              // 🔄 INVALIDAR CACHE DO REACT QUERY para forçar reload das configs
              console.log('🔄 Invalidando cache das configurações...');
              queryClient.invalidateQueries({ queryKey: ['persona-config', userId] });
              queryClient.invalidateQueries({ queryKey: ['produto-config', userId] });
              queryClient.invalidateQueries({ queryKey: ['emojis-config', userId] });
              console.log('✅ Cache invalidado! Configurações serão recarregadas automaticamente.');

              showSuccessToast(`✅ ${scriptsToApprove.length} scripts e configurações aplicados!`, theme);
            } else if (scriptsToApprove.length > 0) {
              showSuccessToast(`✨ ${scriptsToApprove.length} scripts aplicados! Scripts antigos foram substituídos.`, theme);
            } else {
              showSuccessToast('Nenhum script aprovado.', theme);
            }
          } catch (syncError) {
            console.error('❌ Erro ao sincronizar configurações:', syncError);
            // Mesmo com erro na sync, os scripts foram aplicados
            showSuccessToast(`✨ ${scriptsToApprove.length} scripts aplicados! (configurações podem precisar de ajuste manual)`, theme);
          }

          setTimeout(() => { window.__loopraToastLock = false; }, 2000);
        }
      }
    } catch (error) {
      showErrorToast('Erro ao aprovar scripts. Tente novamente.', theme);
    } finally {
      setShowConfirmModal(false);
      setSelectedScript(null);
    }
  };

  const handleReject = (script: GeneratedScript) => {
    setGeneratedScripts((prev) =>
      prev.map((s) => (s.name === script.name && s.type === script.type ? { ...s, status: 'rejected' } : s))
    );
  };

  const approvedCount = generatedScripts.filter((s) => s.status === 'approved').length;
  const canApproveAll = generatedScripts.length > 0 && approvedCount < generatedScripts.filter((s) => s.status !== 'rejected').length;

  return (
    <GeneratorContainer>
      {/* Header do chat */}
      <ChatHeader>
        <ChatLogo>
          <img src="/icons/loopra_head.webp" alt="Loopra AI" />
        </ChatLogo>
        <ChatTitle>
          <h3>
            Loopra AI
          </h3>
          <p>Assistente de Criação de Scripts Inteligente</p>
        </ChatTitle>
      </ChatHeader>

      {/* Área de conteúdo scrollável */}
      <ResultsArea data-results-area>
        {/* Empty State - Placeholder quando não há nada */}
        {!isGenerating && generatedScripts.length === 0 && (
          <EmptyState
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
          >
            <LoopraLoading size={96} />
            <h2>Quem vamos criar hoje?</h2>
          </EmptyState>
        )}
        
        {isGenerating && progress && (
          <GenerationProgress>
            <ProgressHeader>
              <ProgressIcon>
                <Loader2 size={20} color="white" />
              </ProgressIcon>
              <ProgressText>
                <ProgressTitle>{progress.currentScript}</ProgressTitle>
                <ProgressSubtitle>{Math.round(progress.progress)}% concluído</ProgressSubtitle>
              </ProgressText>
            </ProgressHeader>
            <ProgressBar>
              <ProgressFill $progress={progress.progress} />
            </ProgressBar>
            <ThinkingSection 
              $expanded={isThinkingExpanded} 
              onClick={() => setIsThinkingExpanded(!isThinkingExpanded)}
            >
              <ThinkingHeader>
                <ThinkingTitle>
                  <Sparkles size={16} />
                  Cadeia de Pensamento
                </ThinkingTitle>
                {isThinkingExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </ThinkingHeader>
              <ThinkingContent $expanded={isThinkingExpanded}>
                <ThinkingText>{progress.reasoning}</ThinkingText>
              </ThinkingContent>
            </ThinkingSection>
          </GenerationProgress>
        )}

        {canApproveAll && (
          <ApproveAllBar>
            <ApproveAllInfo>
              <ApproveAllTitle>Aprovar Scripts</ApproveAllTitle>
              <ApproveAllSubtitle>
                {generatedScripts.filter((s) => s.status !== 'rejected').length - approvedCount} script(s) pendente(s)
              </ApproveAllSubtitle>
            </ApproveAllInfo>
            <ApproveAllButton onClick={handleApproveAllClick} $disabled={!canApproveAll} disabled={!canApproveAll}>
              <CheckCheck size={20} />
              Aprovar Todos
            </ApproveAllButton>
          </ApproveAllBar>
        )}

        {generatedScripts.length > 0 && (
          <ScriptsGrid>
            {generatedScripts.map((script) => {
              const scriptId = `${script.type}-${script.name}`;
              const isExpanded = expandedScripts.has(scriptId);
              const template = SCRIPT_TEMPLATES[script.type];

              return (
                <ScriptCard key={scriptId} $status={script.status}>
                  <ScriptCardHeader onClick={() => toggleScript(scriptId)}>
                    <ScriptCardTitle>
                      <ScriptTypeIcon $scriptType={script.type}>
                        {React.createElement(template.icon, { size: 20 })}
                      </ScriptTypeIcon>
                      <ScriptInfo>
                        <ScriptName>{script.name}</ScriptName>
                        <ScriptType>{template.title}</ScriptType>
                      </ScriptInfo>
                    </ScriptCardTitle>
                    <ScriptActions onClick={(e) => e.stopPropagation()}>
                      {script.status !== 'approved' && script.status !== 'rejected' && (
                        <>
                          <IconButton $variant="primary" onClick={() => handleApproveClick(script)} title="Aprovar">
                            <Check size={18} />
                          </IconButton>
                          <IconButton $variant="error" onClick={() => handleReject(script)} title="Rejeitar">
                            <X size={18} />
                          </IconButton>
                        </>
                      )}
                      {script.status === 'approved' && (
                        <IconButton $variant="primary" disabled title="Aprovado">
                          <Check size={18} />
                        </IconButton>
                      )}
                      {script.status === 'rejected' && (
                        <IconButton $variant="error" disabled title="Rejeitado">
                          <X size={18} />
                        </IconButton>
                      )}
                      {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    </ScriptActions>
                  </ScriptCardHeader>

                  <ScriptCardContent $expanded={isExpanded}>
                    <ScriptPreview>
                      <PreviewLabel>Preview do Conteúdo</PreviewLabel>
                      <PreviewContent>
                        {Array.isArray(script.content) ? script.content.join('\n') : script.content}
                      </PreviewContent>
                    </ScriptPreview>

                    <ReasoningSection>
                      <ReasoningLabel>
                        <Sparkles size={14} />
                        Cadeia de Raciocínio
                      </ReasoningLabel>
                      <ReasoningText>{script.reasoning}</ReasoningText>
                    </ReasoningSection>
                  </ScriptCardContent>
                </ScriptCard>
              );
            })}
          </ScriptsGrid>
        )}
      </ResultsArea>

      {/* Input fixo embaixo (ChatGPT style) */}
      <PromptArea>
        <InputWrapper>
          <InputField>
            <StyledTextarea
              ref={textareaRef}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Descreva seu atendente: nome, produto, tom de voz, personalidade..."
              disabled={isGenerating}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleGenerate();
                }
              }}
            />
          </InputField>
          <GenerateButton onClick={handleGenerate} $disabled={!prompt.trim() || isGenerating} disabled={!prompt.trim() || isGenerating}>
            {isGenerating ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                Processando...
              </>
            ) : (
              <>
                <Send size={20} />
                Gerar
              </>
            )}
          </GenerateButton>
        </InputWrapper>
      </PromptArea>

      {/* Modal de Confirmação */}
      <AnimatePresence>
        {showConfirmModal && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowConfirmModal(false)}
          >
            <ModalContent
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>
                  <CheckCheck size={24} />
                  {confirmAction === 'all' ? 'Aprovar Todos os Scripts?' : 'Aprovar Script?'}
                </ModalTitle>
                <CloseButton onClick={() => setShowConfirmModal(false)}>
                  <X size={20} />
                </CloseButton>
              </ModalHeader>
              <ModalBody>
                {confirmAction === 'all' ? (
                  <p>
                    Esta ação irá <strong>substituir TODOS os scripts atuais</strong> pelos scripts gerados pela IA.
                    <br /><br />
                    <strong>Total de scripts:</strong> {generatedScripts.filter((s) => s.status !== 'rejected').length}
                    <br /><br />
                    Deseja continuar?
                  </p>
                ) : (
                  <p>
                    Esta ação irá <strong>adicionar ou substituir</strong> o script <strong>"{selectedScript?.name}"</strong>.
                    <br /><br />
                    Se já existir um script com o mesmo nome e tipo, ele será substituído.
                    <br /><br />
                    Deseja continuar?
                  </p>
                )}
              </ModalBody>
              <ModalFooter>
                <ActionButton $variant="cancel" onClick={() => setShowConfirmModal(false)}>
                  Cancelar
                </ActionButton>
                <ActionButton $variant="primary" onClick={handleConfirmApprove}>
                  <Check size={18} />
                  {confirmAction === 'all' ? 'Aprovar Todos' : 'Aprovar'}
                </ActionButton>
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>

      {/* Modal de Resumo do Atendente */}
      <AnimatePresence>
        {showSummaryModal && attendantSummary && (
          <ModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowSummaryModal(false)}
          >
            <ModalContent
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <ModalHeader>
                <ModalTitle>
                  <Sparkles size={16} />
                  Atendente Gerado
                </ModalTitle>
                <CloseButton onClick={() => setShowSummaryModal(false)}>
                  <X size={16} />
                </CloseButton>
              </ModalHeader>
              <ModalBody>
                <SummarySection>
                  <SummaryTitle>
                    <User size={14} />
                    Nome
                  </SummaryTitle>
                  <SummaryText><strong>{attendantSummary.name}</strong></SummaryText>
                </SummarySection>

                <SummarySection>
                  <SummaryTitle>
                    <Brain size={14} />
                    Resumo
                  </SummaryTitle>
                  <SummaryText>{attendantSummary.personality}</SummaryText>
                </SummarySection>

                <SummarySection>
                  <SummaryTitle>
                    <Zap size={14} />
                    Exemplo
                  </SummaryTitle>
                  <ExampleBox>
                    "{attendantSummary.example}"
                  </ExampleBox>
                </SummarySection>
              </ModalBody>
              <ModalFooter>
                <ActionButton $variant="primary" onClick={() => setShowSummaryModal(false)}>
                  <Check size={18} />
                  Entendi
                </ActionButton>
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </GeneratorContainer>
  );
};

export default ScriptGenerator;